/**
 * Math Coach API service
 * Handles HTTP requests to the math coach session endpoint
 */

import { api } from '@/lib/api';
import type { CreateDecisionResponse } from '@/types/decision';

/**
 * Active math coach session data from GET /sessions/math-coach/active
 */
export interface ActiveMathCoachSession {
  /** Session ID, e.g. "MS-2026-03-11-143022" */
  sessionId: string;
  /** Always "active" */
  status: string;
  /** ISO 8601 timestamp */
  createdAt: string;
}

/**
 * Fetch the authenticated user's active math coach session.
 * Returns the session if one exists (200).
 * Throws an error with code 'NOT_FOUND' if no active session exists (404).
 * Errors propagate to the hook layer for handling.
 */
export async function getActiveMathCoachSession(): Promise<ActiveMathCoachSession> {
  const response = await api.get<ActiveMathCoachSession>('/sessions/math-coach/active');
  return response.data;
}

/**
 * Post a math coach message (text or image) to an active session.
 * Always uses role='parent' and decisionType='feedback'.
 *
 * @param sessionId - The active math coach session ID
 * @param userId - The authenticated user's ID (parent sending their own messages)
 * @param content - Text content or base64-encoded image data
 * @param type - 'text' or 'image'
 * @param mimeType - Required when type is 'image' (e.g., 'image/jpeg')
 * @returns Promise resolving to CreateDecisionResponse with decisionId, sessionId, timestamp
 */
export async function postMathCoachMessage(
  sessionId: string,
  userId: string,
  content: string,
  type: 'text' | 'image',
  mimeType?: string
): Promise<CreateDecisionResponse> {
  const body: Record<string, unknown> = {
    studentId: userId,
    sessionId,
    role: 'parent',
    decisionType: 'feedback',
    content,
    type,
  };

  if (type === 'image' && mimeType) {
    body.mimeType = mimeType;
  }

  const response = await api.post<CreateDecisionResponse>('/decisions', body);
  return response.data;
}
